<template>
  <v-dialog :value="value" hide-overlay persistent :width="isMini ? miniWidth : 1300">
    <v-card>
      <v-toolbar dark color="accent" flat>
        <v-toolbar-title class="font-weight-light">Item Search</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$emit('mini', isMini = !isMini)">
          <v-icon>{{ isMini ? 'mdi-window-maximize' : 'mdi-window-minimize' }}</v-icon>
        </v-btn>
        <v-btn class="mx-1" icon depressed @click="$emit('update:value', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider v-if="!isMini" />
      <v-container v-if="!isMini" class="pa-6 fill" fluid>
        <v-row align="center" dense>
          <v-col sm="4" md="3" lg="3" xl="3">
            <v-row dense>
              <v-col>
              <label :class="label">UPC Lookup</label>
              </v-col>
            </v-row>
            <v-textarea :class="body" persistent-hint hint="Enter UPCs on separate lines" v-model="search" no-resize
              outlined dense background-color="input" rows="12" type="number">
              <template #append>
                <v-btn :disabled="!search" rounded small :class="body" class="grey lighten-2 mb-2 ml-4"
                  @click="searchByUPC()">
                  <v-icon left>mdi-magnify</v-icon>Search
                </v-btn>
              </template>
            </v-textarea>
          </v-col>
          <v-col>
            <v-row dense>
              <v-col>
                <label :class="label">Search Results</label>
              </v-col>
            </v-row>
            <v-data-table :headers="headers" :height="tableHeight" :items="results" :loading="loading"
              loading-text="Searching for UPC...Please Wait." :items-per-page="pageSize" dense fixed-header
              hide-default-footer no-results-text="No Matching UPCs" no-data-text="Enter UPC to Display Results"
              v-model="selected" item-key="item_uuid" show-select class="rounded mb-7" style="border: 1px solid #9e9e9e"
              :class="table">

              <template #[`header.data-table-select`]="{ on, props }">
                <v-simple-checkbox :disabled="allExistingItems" :ripple="false" v-on="on" v-bind="props" dark />
              </template>

              <template #[`item.data-table-select`]="{ on, item }">
                <v-icon class="green--text" v-if="items.some(i => i.upc == item.upc)">mdi-check</v-icon>
                <v-simple-checkbox v-else :ripple="false" v-on="on" :value="selected.includes(item)"
                  @click="selected.includes(item) ? deselectItem(item) : selectItem(item)" />
              </template>

              <template #[`item.pack`]="{ item }">
                {{item.pack}} / {{item.size !== 'unknown' ?  item.size : '---'}}
              </template>

            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
      <v-divider v-if="!isMini" />
      <v-toolbar v-if="!isMini" color="fill" flat>
        <v-spacer />
        <v-btn :loading="btnLoader" rounded :class="body" class="primary white--text"
          @click="addItemsToContract">
          <v-icon left>mdi-plus</v-icon>
          Add
        </v-btn>
      </v-toolbar>
      <v-overlay absolute v-model="saving">
        <v-progress-circular indeterminate size="100">
          Saving...
        </v-progress-circular>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
  import {
    table
  } from '@/mixins/table-config'
  import {
    displayHelpers
  } from '@/mixins/display-helpers'
  import {
    mapState
  } from 'vuex'

  export default {
    name: 'ItemSearch',

    mixins: [table, displayHelpers],

    props: {
      value: Boolean,
      contract: Object,
      items: Array,
      getItems: Function,
      saving: Boolean,
      vendor: Object,
      isMini: Boolean,
      miniWidth: Number
    },

    data: () => ({
      btnLoader: false,
      exactItems: [],
      loading: false,
      newItems: [],
      pageSize: 500,
      results: [],
      search: '',
      searching: false,
      searchType: 0,
      selected: [],
      tab: 0,
      tableHeight: 340
    }),

    computed: {
      ...mapState(['allStores', 'currentVendor']),

      storeNumbers () {
        return this.allStores.map(store => store = parseInt(store.StoreNumber))
      },

      upcResults() {
        return this.results.reduce((acc, item) => {
          item = item.upc
          acc.push(item)
          return acc
        }, [])
      },

      allExistingItems () {
        return this._.differenceBy(this.results, this.items, 'upc').length == 0
      },

      headers() {
        return [{
            sortable: false,
            value: 'data-table-select',
            class: 'accent white--text'
          },
          {
            text: 'UPC',
            sortable: true,
            class: 'accent white--text',
            value: 'upc',
            width: 'auto'
          },
          {
            text: 'Description',
            sortable: true,
            class: 'accent white--text',
            value: 'description',
            width: '40%'
          },
          {
            text: 'Pack/Size',
            sortable: true,
            class: 'accent white--text',
            value: 'pack'
          },
          {
            text: '',
            sortable: false,
            filterable: false,
            class: 'accent white--text',
            value: 'clear',
            width: '20%'
          }
        ]
      }
    },

    watch: {
      localStorage: {
        immediate: true,
        handler() {
          if (localStorage.vendor) {
            this.$store.commit('SET_CURRENT_VENDOR', JSON.parse(localStorage.getItem('vendor')))
          }
        }
      }
    },

    created() {
      this.loading = false
    },

    methods: {
      formattedCurrency(value) {
        return parseFloat(value).toFixed(2)
      },

      selectItem(item) {
        this.selected.push(item)
      },

      deselectItem(item) {
        this.selected = this._.reject(this.selected, {
          'upc': item.upc
        })
      },

      async searchByUPC() {
        this.selected = []
        if (this.search) {
          this.loading = true
          const itemLookup = {
            upc_list: this.search.split("\n"),
            vendor: this.vendor.vendor_number,
            store_list: this.storeNumbers
          }
          try {
            const { data } = await this.$items.getItemByUPC(itemLookup)
            this.results = data
            if (!this.results.length) {
              this.$store.dispatch('setSnackbar', { status: 'error', text: 'No Matching UPCs' })
            }
          } catch {
            this.$store.dispatch('setSnackbar', { status: 'error', text: 'Unexpected Error' })
          } finally {
            this.loading = false
          }
        } else {
          this.$store.dispatch('setSnackbar', { status: 'error', text: 'No UPCs Entered' })
        }
      },

      addItemsToContract() {
        this.selected = this._.differenceBy(this.selected, this.items, 'upc')
        if (this.selected.length) {
          this.$emit('add', { newItem: false, data: this.selected })
        } else {
          this.$store.dispatch('setSnackbar', { status: 'error', text: 'No Items Selected' })
        }
        this.selected = []
        this.searchByUPC()
      }
    },

  }
</script>
<style scoped>

</style>